import { Request, StreamingRequest } from "./HttpService";

// Internal streaming state management
const streamingState = {
  activeStreams: new Map(), // streamId -> abortController
  currentStreamId: null
};

export const IntelligentSearchService = {
  /**
   * Get AI-powered candidate recommendations based on job requirements
   * @param {Object} requestData - The recommendation request data
   * @param {string} requestData.jobRequirements - Job requirements and description
   * @param {string} requestData.company - Optional company filter
   * @param {number} requestData.maxCandidates - Maximum number of candidates (1-20)
   * @param {string} requestData.additionalContext - Additional context for recommendations
   * @returns {Promise} Response containing candidate recommendations
   */
  getCandidateRecommendations(requestData) {
    return Request("vitae/semanticsearch/recommendations", "POST", requestData);
  },

  /**
   * Start streaming AI-powered candidate recommendations in real-time
   * Service manages AbortController internally for clean component usage
   * @param {Object} requestData - The recommendation request data
   * @param {string} requestData.jobRequirements - Job requirements and description
   * @param {number} requestData.dataSource - Data source (0 = Our Database, 1 = CV Files)
   * @param {string} requestData.additionalContext - Additional context for recommendations
   * @param {Object} callbacks - Callback functions for handling streaming events
   * @param {Function} callbacks.onCandidate - Called when a new candidate is received
   * @param {Function} callbacks.onComplete - Called when streaming completes
   * @param {Function} callbacks.onError - Called when an error occurs
   * @param {Function} callbacks.onProgress - Optional progress callback with candidate count
   * @param {string} streamId - Optional unique identifier for this stream (auto-generated if not provided)
   * @returns {Promise<string>} Promise that resolves to streamId when streaming starts
   */
  async streamCandidateRecommendations(
    requestData,
    callbacks,
    streamId = null
  ) {
    // Generate unique stream ID if not provided
    const currentStreamId =
      streamId ||
      `stream_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Create abort controller for this stream
    const abortController = new AbortController();

    // Store the controller
    streamingState.activeStreams.set(currentStreamId, abortController);
    streamingState.currentStreamId = currentStreamId;

    const { onCandidate, onComplete, onError, onProgress } = callbacks;
    let candidateCount = 0;

    try {
      await StreamingRequest(
        "vitae/semanticsearch/recommendations/stream",
        requestData,

        // onData callback - receives each candidate
        (candidate) => {
          candidateCount++;

          // Call progress callback if provided
          if (onProgress) {
            onProgress(candidateCount, candidate);
          }

          // Call candidate callback
          if (onCandidate) {
            onCandidate(candidate, candidateCount);
          }
        },

        // onComplete callback
        () => {
          // Clean up stream from internal state
          streamingState.activeStreams.delete(currentStreamId);
          if (streamingState.currentStreamId === currentStreamId) {
            streamingState.currentStreamId = null;
          }

          if (onComplete) {
            onComplete(candidateCount);
          }
        },

        // onError callback
        (error) => {
          // Clean up stream from internal state
          streamingState.activeStreams.delete(currentStreamId);
          if (streamingState.currentStreamId === currentStreamId) {
            streamingState.currentStreamId = null;
          }

          if (onError) {
            onError(error, candidateCount);
          }
        },

        abortController
      );

      return currentStreamId;
    } catch (error) {
      // Clean up on immediate error
      streamingState.activeStreams.delete(currentStreamId);
      if (streamingState.currentStreamId === currentStreamId) {
        streamingState.currentStreamId = null;
      }
      throw error;
    }
  },

  /**
   * Start streaming with integrated state management for React components
   * This method handles all state updates internally, making component usage extremely simple
   * @param {Object} requestData - The recommendation request data
   * @param {string} requestData.jobRequirements - Job requirements and description
   * @param {number} requestData.dataSource - Data source (0 = Our Database, 1 = CV Files)
   * @param {string} requestData.additionalContext - Additional context for recommendations
   * @param {Function} setStreamingState - React state setter function for streaming state
   * @param {string} streamId - Optional unique identifier for this stream (auto-generated if not provided)
   * @returns {Promise<string>} Promise that resolves to streamId when streaming starts
   */
  async streamWithStateManagement(
    requestData,
    setStreamingState,
    streamId = null
  ) {
    // Generate unique stream ID if not provided
    const currentStreamId =
      streamId ||
      `stream_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

    // Create abort controller for this stream
    const abortController = new AbortController();

    // Store the controller
    streamingState.activeStreams.set(currentStreamId, abortController);
    streamingState.currentStreamId = currentStreamId;

    // Initialize streaming state
    setStreamingState(
      null,
      true,
      `🔍 Starting analysis...`,
      currentStreamId,
      requestData.maxCandidates || 20
    );

    let candidateCount = 0;

    try {
      await StreamingRequest(
        "vitae/semanticsearch/recommendations/stream",
        requestData,

        // onData callback - receives each candidate
        (candidate) => {
          candidateCount++; // Increment the counter
          setStreamingState(
            candidate,
            null,
            null,
            null,
            requestData.maxCandidates || 20
          );
        },

        // onComplete callback
        () => {
          // Clean up stream from internal state
          streamingState.activeStreams.delete(currentStreamId);
          if (streamingState.currentStreamId === currentStreamId) {
            streamingState.currentStreamId = null;
          }

          // Update state for completion
          setStreamingState(
            null,
            false,
            `✅ Analysis complete! Found ${candidateCount} candidates.`,
            null,
            requestData.maxCandidates || 20
          );
        },

        // onError callback
        (error) => {
          // Clean up stream from internal state
          streamingState.activeStreams.delete(currentStreamId);
          if (streamingState.currentStreamId === currentStreamId) {
            streamingState.currentStreamId = null;
          }

          // Update state for error
          setStreamingState(
            null,
            false,
            `❌ Error: ${error.message} (Analyzed ${candidateCount} candidates)`,
            null,
            requestData.maxCandidates || 20
          );
        },

        abortController
      );

      return currentStreamId;
    } catch (error) {
      // Clean up on immediate error
      streamingState.activeStreams.delete(currentStreamId);
      if (streamingState.currentStreamId === currentStreamId) {
        streamingState.currentStreamId = null;
      }

      // Update state for startup error
      setStreamingState(
        null,
        false,
        `❌ Failed to start analysis: ${error.message}`,
        null
      );

      throw error;
    }
  },

  /**
   * Stop a specific streaming request with optional state management
   * @param {string} streamId - The stream ID to stop (optional, stops current stream if not provided)
   * @param {Function} setStreamingState - Optional React state setter for automatic cleanup
   * @returns {boolean} True if stream was found and stopped, false otherwise
   */
  stopStreamWithState(streamId = null, setStreamingState = null) {
    const targetStreamId = streamId || streamingState.currentStreamId;

    if (!targetStreamId) {
      console.warn("No active stream to stop");
      return false;
    }

    const abortController = streamingState.activeStreams.get(targetStreamId);
    if (abortController) {
      abortController.abort();
      streamingState.activeStreams.delete(targetStreamId);

      if (streamingState.currentStreamId === targetStreamId) {
        streamingState.currentStreamId = null;
      }

      // Automatically update state if setter provided
      if (setStreamingState) {
        setStreamingState((prev) => ({
          ...prev,
          isStreaming: false,
          status: "⏹️ Analysis stopped by user",
          streamId: null
        }));
      }

      return true;
    }

    return false;
  },

  /**
   * Stop a specific streaming request
   * @param {string} streamId - The stream ID to stop (optional, stops current stream if not provided)
   * @returns {boolean} True if stream was found and stopped, false otherwise
   */
  stopStream(streamId = null) {
    const targetStreamId = streamId || streamingState.currentStreamId;

    if (!targetStreamId) {
      console.warn("No active stream to stop");
      return false;
    }

    const abortController = streamingState.activeStreams.get(targetStreamId);
    if (abortController) {
      abortController.abort();
      streamingState.activeStreams.delete(targetStreamId);

      if (streamingState.currentStreamId === targetStreamId) {
        streamingState.currentStreamId = null;
      }

      return true;
    }

    return false;
  },

  /**
   * Stop all active streaming requests
   * @returns {number} Number of streams that were stopped
   */
  stopAllStreams() {
    let stoppedCount = 0;

    for (const [streamId, abortController] of streamingState.activeStreams) {
      abortController.abort();
      stoppedCount++;
    }

    streamingState.activeStreams.clear();
    streamingState.currentStreamId = null;

    return stoppedCount;
  },

  /**
   * Check if there are any active streaming requests
   * @returns {boolean} True if streaming is active
   */
  isStreaming() {
    return streamingState.activeStreams.size > 0;
  },

  /**
   * Get the current active stream ID
   * @returns {string|null} Current stream ID or null if no active stream
   */
  getCurrentStreamId() {
    return streamingState.currentStreamId;
  },

  /**
   * Get all active stream IDs
   * @returns {string[]} Array of active stream IDs
   */
  getActiveStreamIds() {
    return Array.from(streamingState.activeStreams.keys());
  },

  /**
   * Perform intelligent search on CV documents
   * @param {Object} searchData - The search request data
   * @param {string} searchData.query - Search query text
   * @param {string} searchData.company - Optional company filter
   * @param {number} searchData.top - Maximum number of results (1-100)
   * @param {boolean} searchData.useSemanticSearch - Whether to use intelligent search
   * @returns {Promise} Response containing search results
   */
  performIntelligentSearch(searchData) {
    return Request("vitae/semanticsearch/search", "POST", searchData);
  },

  /**
   * Get available companies for search filtering
   * @returns {Promise} Response containing list of companies
   */
  getAvailableCompanies() {
    return Request("vitae/semanticsearch/companies", "GET");
  }
};
